import React, { useEffect, useState } from "react";
import { CalendarClock, Check, CreditCard, Loader2, Minus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHead, Panel } from "@/pages/vendor/Shell";
import { Button } from "@/components/ui/button";

const Feature = ({ on, children }: { on: boolean; children: React.ReactNode }) => (
  <li className={`flex items-center gap-2 text-sm ${on ? "" : "text-muted-foreground line-through"}`}>
    {on ? <Check className="h-4 w-4 text-emerald-600" /> : <Minus className="h-4 w-4" />} {children}
  </li>
);

export default function Billing({ embedded }: { embedded?: boolean }) {
  const { vendor } = useAuth();
  const isOwner = vendor.user.staff_role === "owner";
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/billing/plans").then((r) => setD(r.data)); }, []);

  const checkout = async (plan: string) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };
  if (!d) return <p className="text-muted-foreground">Loading...</p>;
  const trialPast = d.plan === "trial" && new Date(d.trial_ends_at) < new Date();
  const trialEnds = d.plan === "trial" ? `${trialPast ? "trial ended" : "trial ends"} ${new Date(d.trial_ends_at).toLocaleDateString()}` : null;
  const sub = d.subscription;
  const fmt = (s: string) => new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const portal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy("");
    }
  };
  const resume = async () => {
    setBusy("resume");
    try {
      const { data } = await api.post("/billing/resume");
      setD({ ...d, subscription: data.subscription });
      toast.success("Your subscription will keep renewing");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };

  return (
    <div>
      {!embedded && <PageHead eyebrow="Subscription" title="Billing" />}
      <Panel className="mb-5 space-y-3" data-testid="billing-current-plan">
        <div>
          <p className="text-sm text-muted-foreground">Current plan</p>
          <p className="text-xl font-bold">{d.current.name}{trialEnds ? ` · ${trialEnds}` : ""}</p>
          {sub && !sub.cancel_at && sub.current_period_end && <p className="text-sm text-muted-foreground" data-testid="billing-renews-on">Renews on {fmt(sub.current_period_end)}</p>}
          {sub?.cancel_at && (
            <p className="mt-1 inline-flex items-center gap-1.5 rounded-sm bg-amber-50 px-2 py-1 text-sm font-semibold text-amber-800" data-testid="billing-cancels-on">
              <CalendarClock className="h-4 w-4" /> Cancels on {fmt(sub.cancel_at)}
            </p>
          )}
        </div>
        {sub && isOwner && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={portal} disabled={!!busy} variant="secondary" data-testid="billing-manage-button">
              {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="mr-1.5 h-4 w-4" /> Manage subscription</>}
            </Button>
            {sub.cancel_at && (
              <Button onClick={resume} disabled={!!busy} className="bg-primary text-white hover:bg-primary/90" data-testid="billing-resume-button">
                {busy === "resume" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resume subscription"}
              </Button>
            )}
          </div>
        )}
        {sub && isOwner && <p className="text-xs text-muted-foreground">Update your card, download invoices or cancel on Stripe's secure billing page.</p>}
      </Panel>
      <div className="grid max-w-md gap-4">
        {d.plans.map((p: any) => {
          const current = d.plan === p.key;
          return (
            <Panel key={p.key} className="rise flex flex-col border-emerald-500" data-testid={`plan-card-${p.key}`}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Everything included</p>
              <h3 className="mt-1 text-2xl font-semibold">{p.name}</h3>
              <p className="mt-2"><span className="font-mono text-4xl font-semibold">${p.price}</span><span className="text-muted-foreground"> /month</span></p>
              <ul className="my-5 flex-1 space-y-2">
                <Feature on>{p.seats} staff seats</Feature>
                <Feature on>{p.events == null ? "Unlimited events" : `${p.events} active events`}</Feature>
                <Feature on>Lead pipeline, notes, chat</Feature>
                <Feature on={p.forms}>Extra booth questions</Feature>
                <Feature on={p.export}>CSV export</Feature>
                <Feature on={p.messaging}>Email / SMS / WhatsApp follow-up</Feature>
                <Feature on={p.staff_activity}>Staff activity analytics</Feature>
              </ul>
              <Button disabled={!isOwner || current || !!busy} onClick={() => checkout(p.key)} data-testid={`plan-subscribe-${p.key}`}
                className="bg-primary text-white hover:bg-primary/90">
                {busy === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : current ? "Current plan" : `Choose ${p.name}`}
              </Button>
            </Panel>
          );
        })}
      </div>
      {!isOwner && <p className="mt-4 text-sm text-muted-foreground">Only the account owner can change the plan.</p>}
    </div>
  );
}
